import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Sparkles, Loader2, ChefHat, Wine, IceCream, Sun, Snowflake, Leaf, TreePine, RefreshCw, Star, TrendingUp, Minus, Plus, Target, Calendar, PartyPopper, ShoppingCart, BookOpen, Recycle, Clock, Thermometer, AlertTriangle, X } from 'lucide-react';

const SEASON_ICONS = { hiver: Snowflake, printemps: Leaf, ete: Sun, automne: TreePine };
const SEASON_LABELS = { hiver: 'Hiver', printemps: 'Printemps', ete: 'Été', automne: 'Automne' };
const SEASON_COLORS = {
  hiver: 'rgb(100, 149, 237)',
  printemps: 'rgb(var(--lm-green))',
  ete: 'rgb(var(--lm-gold))',
  automne: 'rgb(var(--lm-orange))',
};

const RecipeCard = ({ item }) => (
  <div className="lm-card p-4 hover:border-[rgb(var(--lm-green)/0.3)] transition-colors" data-testid="recipe-card">
    <div className="flex justify-between items-start mb-1.5">
      <h4 className="text-sm font-semibold text-[rgb(var(--lm-text))]">{item.nom}</h4>
      <div className="flex items-center gap-1.5 shrink-0 ml-2">
        {item.temps_prep && (
          <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-blue-400/10 text-blue-400 flex items-center gap-0.5">
            <Clock size={8} />{item.temps_prep}mn
          </span>
        )}
        {item.cout_matiere != null && (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]">
            {item.cout_matiere.toFixed ? item.cout_matiere.toFixed(1) : item.cout_matiere}€
          </span>
        )}
      </div>
    </div>
    <p className="text-xs text-[rgb(var(--lm-text-secondary))] leading-relaxed">{item.description}</p>
    {item.ingredients && (
      <div className="flex flex-wrap gap-1 mt-2">
        {item.ingredients.map((ing, i) => (
          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))]">{typeof ing === 'string' ? ing : ing.nom}</span>
        ))}
      </div>
    )}
    <div className="flex items-center gap-2 mt-2">
      {item.difficulte && (
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
          item.difficulte === 'facile' ? 'bg-green-400/10 text-green-400' :
          item.difficulte === 'moyen' ? 'bg-orange-400/10 text-orange-400' :
          'bg-red-400/10 text-red-400'
        }`}>{item.difficulte}</span>
      )}
      {item.prix_suggere != null && (
        <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">
          Prix : <span className="font-mono text-[rgb(var(--lm-gold))]">{item.prix_suggere}€</span>
          {item.marge_pct != null && <span> · Marge {item.marge_pct}%</span>}
        </span>
      )}
    </div>
    {item.prix_vente_suggere != null && (
      <p className="text-[10px] mt-2 text-[rgb(var(--lm-text-muted))]">
        Prix suggere : <span className="font-mono text-[rgb(var(--lm-gold))]">{item.prix_vente_suggere}€</span>
      </p>
    )}
    {item.raison && <p className="text-[10px] mt-1 italic text-[rgb(var(--lm-text-muted))]">{item.raison}</p>}
  </div>
);

const SectionTitle = ({ icon: Icon, title, color }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={16} style={{ color }} />
    <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">{title}</h3>
  </div>
);

export default function ChefRecettes() {
  const [activeTab, setActiveTab] = useState('menu');
  const [loading, setLoading] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [weekendData, setWeekendData] = useState(null);
  const [carteData, setCarteData] = useState(null);
  const [quantitesData, setQuantitesData] = useState(null);
  const [ficheData, setFicheData] = useState(null);
  const [gaspiData, setGaspiData] = useState(null);
  const [budgetConfig, setBudgetConfig] = useState({ budget_par_couvert: 8, budget_weekend: 12, objectif_food_cost: 30 });
  const [showConfig, setShowConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Quantites inputs
  const [nbCouverts, setNbCouverts] = useState(40);

  // Fiche technique inputs
  const [fichePlat, setFichePlat] = useState('');
  const [fichePortions, setFichePortions] = useState(10);

  // Anti-gaspi inputs
  const [restesInput, setRestesInput] = useState('');
  const [restesList, setRestesList] = useState([]);

  useEffect(() => {
    api.get('/recipes/budget-config').then(r => setBudgetConfig(r.data)).catch(() => {});
  }, []);

  const generate = async (type, body = {}) => {
    setLoading(true);
    try {
      const res = await api.post(`/recipes/${type}`, body);
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        if (type === 'menu-du-jour') setMenuData(res.data);
        else if (type === 'weekend') setWeekendData(res.data);
        else if (type === 'carte') setCarteData(res.data);
        else if (type === 'quantites') setQuantitesData(res.data);
        else if (type === 'fiche-technique') setFicheData(res.data);
        else if (type === 'anti-gaspi') setGaspiData(res.data);
        toast.success('Suggestions generees');
      }
    } catch { toast.error('Erreur generation'); }
    setLoading(false);
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.post('/recipes/budget-config', budgetConfig);
      toast.success('Budget mis a jour');
      setShowConfig(false);
    } catch { toast.error('Erreur sauvegarde'); }
    setSavingConfig(false);
  };

  const addReste = () => {
    const r = restesInput.trim();
    if (r && !restesList.includes(r)) {
      setRestesList(prev => [...prev, r]);
      setRestesInput('');
    }
  };

  const removeReste = (idx) => {
    setRestesList(prev => prev.filter((_, i) => i !== idx));
  };

  const season = menuData?.season || weekendData?.season || carteData?.season || 'printemps';
  const SeasonIcon = SEASON_ICONS[season] || Sun;
  const seasonColor = SEASON_COLORS[season] || 'rgb(var(--lm-green))';

  const tabs = [
    { id: 'menu', label: 'Menu', icon: ChefHat },
    { id: 'weekend', label: 'Week-end', icon: PartyPopper },
    { id: 'carte', label: 'Carte', icon: Star },
    { id: 'quantites', label: 'Quantites', icon: ShoppingCart },
    { id: 'fiches', label: 'Fiches', icon: BookOpen },
    { id: 'gaspi', label: 'Anti-gaspi', icon: Recycle },
  ];

  return (
    <div className="space-y-4" data-testid="chef-recettes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Sparkles size={20} style={{ color: 'rgb(var(--lm-green))' }} />
            Recettes Mas
          </h2>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-0.5 flex items-center gap-1">
            <SeasonIcon size={12} style={{ color: seasonColor }} />
            Saison : {SEASON_LABELS[season] || season}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowConfig(!showConfig)}
          className="text-xs text-[rgb(var(--lm-text-secondary))] border-[rgb(var(--lm-border))]"
          data-testid="config-toggle-btn">
          <Target size={14} className="mr-1" /> Budget
        </Button>
      </div>

      {/* Budget Config */}
      {showConfig && (
        <div className="lm-card p-4 border-[rgb(var(--lm-green)/0.3)] animate-slide-up" data-testid="budget-config">
          <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Parametres budget</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Budget/couvert semaine</label>
              <div className="flex items-center gap-1">
                <Input type="number" value={budgetConfig.budget_par_couvert}
                  onChange={e => setBudgetConfig(p => ({ ...p, budget_par_couvert: parseFloat(e.target.value) || 0 }))}
                  className="h-8 text-sm font-mono bg-[rgb(var(--lm-bg))]" data-testid="budget-semaine-input" />
                <span className="text-xs text-[rgb(var(--lm-text-muted))]">€</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Budget/couvert week-end</label>
              <div className="flex items-center gap-1">
                <Input type="number" value={budgetConfig.budget_weekend}
                  onChange={e => setBudgetConfig(p => ({ ...p, budget_weekend: parseFloat(e.target.value) || 0 }))}
                  className="h-8 text-sm font-mono bg-[rgb(var(--lm-bg))]" data-testid="budget-weekend-input" />
                <span className="text-xs text-[rgb(var(--lm-text-muted))]">€</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Objectif food cost</label>
              <div className="flex items-center gap-1">
                <Input type="number" value={budgetConfig.objectif_food_cost}
                  onChange={e => setBudgetConfig(p => ({ ...p, objectif_food_cost: parseFloat(e.target.value) || 0 }))}
                  className="h-8 text-sm font-mono bg-[rgb(var(--lm-bg))]" data-testid="foodcost-input" />
                <span className="text-xs text-[rgb(var(--lm-text-muted))]">%</span>
              </div>
            </div>
          </div>
          <Button onClick={saveConfig} disabled={savingConfig} size="sm"
            className="mt-3 bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] hover:bg-[rgb(var(--lm-green)/0.9)] text-xs"
            data-testid="save-config-btn">
            {savingConfig ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
            Sauvegarder
          </Button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[rgb(var(--lm-bg))] overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              activeTab === t.id
                ? 'bg-[rgb(var(--lm-card))] text-[rgb(var(--lm-green))] shadow-sm'
                : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'
            }`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Menu du Jour */}
      {activeTab === 'menu' && (
        <div className="space-y-4 animate-fade-in">
          <Button onClick={() => generate('menu-du-jour')} disabled={loading}
            className="w-full bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] hover:bg-[rgb(var(--lm-green)/0.9)]"
            data-testid="generate-menu-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
            Generer plein de suggestions
          </Button>
          {menuData?.result && (
            <div className="space-y-5 animate-slide-up">
              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="lm-card p-4 text-center border-[rgb(var(--lm-green)/0.2)]">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Menu suggere</p>
                  <p className="font-mono text-2xl text-[rgb(var(--lm-green))] mt-1">{menuData.result.prix_menu_suggere}€</p>
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1">Cout matiere : {menuData.result.cout_total_matiere}€</p>
                </div>
                {menuData.result.astuce_chef && (
                  <div className="lm-card p-4 text-center border-[rgb(var(--lm-gold)/0.2)] flex flex-col justify-center">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-gold))]">Astuce Chef</p>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1 italic">{menuData.result.astuce_chef}</p>
                  </div>
                )}
              </div>

              {/* Menus complets */}
              {menuData.result.menus_complets?.length > 0 && (
                <div>
                  <SectionTitle icon={Target} title={`Menus complets (${menuData.result.menus_complets.length})`} color="rgb(var(--lm-green))" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {menuData.result.menus_complets.map((m, i) => (
                      <div key={i} className="lm-card p-4 border-[rgb(var(--lm-green)/0.15)]">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))] font-medium">Menu {i + 1}</span>
                          <span className="font-mono text-lg text-[rgb(var(--lm-green))] font-bold">{m.prix_menu}€</span>
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-xs"><span className="text-[rgb(var(--lm-text-muted))]">Entree :</span> <span className="text-[rgb(var(--lm-text))]">{m.entree}</span></p>
                          <p className="text-xs"><span className="text-[rgb(var(--lm-text-muted))]">Plat :</span> <span className="text-[rgb(var(--lm-text))]">{m.plat}</span></p>
                          <p className="text-xs"><span className="text-[rgb(var(--lm-text-muted))]">Dessert :</span> <span className="text-[rgb(var(--lm-text))]">{m.dessert}</span></p>
                        </div>
                        {m.cout_total && <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-2 font-mono">Cout matiere: {m.cout_total}€</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Entrees */}
              <div>
                <SectionTitle icon={Leaf} title={`Entrees (${menuData.result.entrees?.length || 0})`} color={SEASON_COLORS.printemps} />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {menuData.result.entrees?.map((e, i) => <RecipeCard key={i} item={e} />)}
                </div>
              </div>

              {/* Plats */}
              <div>
                <SectionTitle icon={ChefHat} title={`Plats (${menuData.result.plats?.length || 0})`} color="rgb(var(--lm-green))" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {menuData.result.plats?.map((p, i) => <RecipeCard key={i} item={p} />)}
                </div>
              </div>

              {/* Desserts */}
              <div>
                <SectionTitle icon={IceCream} title={`Desserts (${menuData.result.desserts?.length || 0})`} color="rgb(var(--lm-gold))" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {menuData.result.desserts?.map((d, i) => <RecipeCard key={i} item={d} />)}
                </div>
              </div>

              {/* Plats uniques */}
              {menuData.result.plats_uniques?.length > 0 && (
                <div>
                  <SectionTitle icon={Clock} title={`Plats uniques / Formule midi (${menuData.result.plats_uniques.length})`} color="rgb(var(--lm-orange))" />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {menuData.result.plats_uniques.map((p, i) => <RecipeCard key={i} item={p} />)}
                  </div>
                </div>
              )}

              {menuData.result.conseil && (
                <div className="lm-card p-3 bg-[rgb(var(--lm-green)/0.05)] border-[rgb(var(--lm-green)/0.2)]">
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                    <Sparkles size={12} className="inline mr-1 text-[rgb(var(--lm-green))]" />
                    {menuData.result.conseil}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Weekend */}
      {activeTab === 'weekend' && (
        <div className="space-y-4 animate-fade-in">
          <Button onClick={() => generate('weekend')} disabled={loading}
            className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:bg-[rgb(var(--lm-gold)/0.9)]"
            data-testid="generate-weekend-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <PartyPopper size={16} className="mr-2" />}
            Suggestions week-end
          </Button>
          {weekendData?.result && (
            <div className="space-y-4 animate-slide-up">
              <div className="lm-card p-4 text-center border-[rgb(var(--lm-gold)/0.2)]">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Ticket moyen vise</p>
                <p className="font-mono text-2xl text-[rgb(var(--lm-gold))] mt-1">{weekendData.result.ticket_moyen_vise}€</p>
              </div>
              {weekendData.result.amuse_bouche && (
                <div>
                  <SectionTitle icon={Star} title="Amuse-bouche offert" color="rgb(var(--lm-gold))" />
                  <RecipeCard item={weekendData.result.amuse_bouche} />
                </div>
              )}
              <div>
                <SectionTitle icon={Leaf} title="Entrees premium" color={SEASON_COLORS.printemps} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {weekendData.result.entrees?.map((e, i) => <RecipeCard key={i} item={e} />)}
                </div>
              </div>
              <div>
                <SectionTitle icon={ChefHat} title="Plats signature" color="rgb(var(--lm-green))" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {weekendData.result.plats?.map((p, i) => <RecipeCard key={i} item={p} />)}
                </div>
              </div>
              <div>
                <SectionTitle icon={IceCream} title="Desserts gourmands" color="rgb(var(--lm-gold))" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {weekendData.result.desserts?.map((d, i) => <RecipeCard key={i} item={d} />)}
                </div>
              </div>
              {weekendData.result.conseil && (
                <div className="lm-card p-3 bg-[rgb(var(--lm-gold)/0.05)] border-[rgb(var(--lm-gold)/0.2)]">
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                    <Sparkles size={12} className="inline mr-1 text-[rgb(var(--lm-gold))]" />{weekendData.result.conseil}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Carte */}
      {activeTab === 'carte' && (
        <div className="space-y-4 animate-fade-in">
          <Button onClick={() => generate('carte')} disabled={loading}
            className="w-full bg-lemas-gold/15 text-lemas-gold hover:bg-lemas-gold/25 border border-lemas-gold/30"
            data-testid="generate-carte-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Star size={16} className="mr-2" />}
            Idees pour la carte
          </Button>
          {carteData?.result && (
            <div className="space-y-4 animate-slide-up">
              {carteData.result.a_ajouter?.length > 0 && (
                <div>
                  <SectionTitle icon={Plus} title="A ajouter" color="rgb(var(--lm-green))" />
                  <div className="space-y-3">
                    {carteData.result.a_ajouter.map((item, i) => <RecipeCard key={i} item={item} />)}
                  </div>
                </div>
              )}
              {carteData.result.a_modifier?.length > 0 && (
                <div>
                  <SectionTitle icon={RefreshCw} title="A modifier" color="rgb(var(--lm-orange))" />
                  <div className="space-y-2">
                    {carteData.result.a_modifier.map((item, i) => (
                      <div key={i} className="lm-card p-3">
                        <p className="text-sm font-medium text-[rgb(var(--lm-text))]">{item.nom_actuel}</p>
                        <p className="text-xs text-[rgb(var(--lm-orange))] mt-1">{item.suggestion}</p>
                        <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1 italic">{item.raison}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {carteData.result.a_retirer?.length > 0 && (
                <div>
                  <SectionTitle icon={Minus} title="A retirer" color="rgb(var(--lm-red))" />
                  <div className="space-y-2">
                    {carteData.result.a_retirer.map((item, i) => (
                      <div key={i} className="lm-card p-3 border-[rgb(var(--lm-red)/0.2)]">
                        <p className="text-sm font-medium text-[rgb(var(--lm-red))]">{item.nom}</p>
                        <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">{item.raison}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {carteData.result.tendances && (
                <div className="lm-card p-3">
                  <SectionTitle icon={TrendingUp} title="Tendances food" color="rgb(var(--lm-gold))" />
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{carteData.result.tendances}</p>
                </div>
              )}
              {carteData.result.conseil && (
                <div className="lm-card p-3 bg-[rgb(var(--lm-green)/0.05)] border-[rgb(var(--lm-green)/0.2)]">
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                    <Sparkles size={12} className="inline mr-1 text-[rgb(var(--lm-green))]" />{carteData.result.conseil}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quantites */}
      {activeTab === 'quantites' && (
        <div className="space-y-4 animate-fade-in">
          <div className="lm-card p-4">
            <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Combien de couverts ?</h3>
            <div className="flex items-center gap-3">
              <button onClick={() => setNbCouverts(Math.max(1, nbCouverts - 5))}
                className="w-8 h-8 rounded-lg bg-[rgb(var(--lm-bg))] flex items-center justify-center text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
                <Minus size={14} />
              </button>
              <Input type="number" value={nbCouverts} onChange={e => setNbCouverts(parseInt(e.target.value) || 1)}
                className="w-20 h-8 text-center text-lg font-mono bg-[rgb(var(--lm-bg))]" data-testid="nb-couverts-input" />
              <button onClick={() => setNbCouverts(nbCouverts + 5)}
                className="w-8 h-8 rounded-lg bg-[rgb(var(--lm-bg))] flex items-center justify-center text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
                <Plus size={14} />
              </button>
              <span className="text-sm text-[rgb(var(--lm-text-secondary))]">couverts</span>
            </div>
          </div>

          <Button onClick={() => generate('quantites', { nb_couverts: nbCouverts })} disabled={loading}
            className="w-full bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] hover:bg-[rgb(var(--lm-green)/0.9)]"
            data-testid="generate-quantites-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShoppingCart size={16} className="mr-2" />}
            Calculer les quantites
          </Button>

          {quantitesData?.result && (
            <div className="space-y-4 animate-slide-up">
              <div className="grid grid-cols-2 gap-3">
                <div className="lm-card p-4 text-center">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Cout total</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-green))] mt-1">{quantitesData.result.cout_total_estime}€</p>
                </div>
                <div className="lm-card p-4 text-center">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))]">Par couvert</p>
                  <p className="font-mono text-xl text-[rgb(var(--lm-gold))] mt-1">{quantitesData.result.cout_par_couvert}€</p>
                </div>
              </div>

              <div className="lm-card overflow-hidden">
                <div className="p-3 border-b border-[rgb(var(--lm-border))]">
                  <SectionTitle icon={ShoppingCart} title={`Liste de courses — ${quantitesData.result.nb_couverts} couverts`} color="rgb(var(--lm-green))" />
                </div>
                <div className="divide-y divide-[rgb(var(--lm-border))]">
                  {quantitesData.result.ingredients?.map((ing, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center justify-between hover:bg-[rgb(var(--lm-bg)/0.5)]" data-testid={`ingredient-${i}`}>
                      <div className="flex-1">
                        <p className="text-sm text-[rgb(var(--lm-text))]">{ing.nom}</p>
                        {ing.fournisseur && <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{ing.fournisseur}</p>}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-mono text-[rgb(var(--lm-text-secondary))]">{ing.quantite}</span>
                        <span className="text-xs font-mono text-[rgb(var(--lm-green))] min-w-[50px] text-right">{ing.prix_estime}€</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {quantitesData.result.conseil_commande && (
                <div className="lm-card p-3 bg-[rgb(var(--lm-green)/0.05)] border-[rgb(var(--lm-green)/0.2)]">
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                    <Sparkles size={12} className="inline mr-1 text-[rgb(var(--lm-green))]" />
                    {quantitesData.result.conseil_commande}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Fiches Techniques */}
      {activeTab === 'fiches' && (
        <div className="space-y-4 animate-fade-in">
          <div className="lm-card p-4">
            <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Fiche technique</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Nom du plat</label>
                <Input value={fichePlat} onChange={e => setFichePlat(e.target.value)}
                  placeholder="Ex: Filet de dorade, sauce vierge"
                  className="h-9 text-sm bg-[rgb(var(--lm-bg))]" data-testid="fiche-plat-input" />
              </div>
              <div>
                <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Nombre de portions</label>
                <Input type="number" value={fichePortions} onChange={e => setFichePortions(parseInt(e.target.value) || 1)}
                  className="h-9 text-sm font-mono bg-[rgb(var(--lm-bg))] w-24" data-testid="fiche-portions-input" />
              </div>
            </div>
          </div>

          <Button onClick={() => generate('fiche-technique', { nom_plat: fichePlat, nb_portions: fichePortions })}
            disabled={loading || !fichePlat.trim()}
            className="w-full bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] hover:bg-[rgb(var(--lm-green)/0.9)]"
            data-testid="generate-fiche-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <BookOpen size={16} className="mr-2" />}
            Generer la fiche technique
          </Button>

          {ficheData?.result && (
            <div className="space-y-4 animate-slide-up">
              <div className="lm-card p-4 border-[rgb(var(--lm-green)/0.2)]">
                <h3 className="text-lg font-semibold text-[rgb(var(--lm-text))] mb-1">{ficheData.result.nom}</h3>
                <div className="flex gap-4 mt-2">
                  <span className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))]">
                    <Clock size={12} /> Prep: {ficheData.result.temps_preparation}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))]">
                    <Clock size={12} /> Cuisson: {ficheData.result.temps_cuisson}
                  </span>
                  {ficheData.result.temperature && (
                    <span className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))]">
                      <Thermometer size={12} /> {ficheData.result.temperature}
                    </span>
                  )}
                </div>
                <div className="flex gap-3 mt-2">
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]">
                    {ficheData.result.cout_matiere_portion}€/portion
                  </span>
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-[rgb(var(--lm-gold)/0.1)] text-[rgb(var(--lm-gold))]">
                    Total: {ficheData.result.cout_matiere_total}€
                  </span>
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <SectionTitle icon={ShoppingCart} title={`Ingredients — ${ficheData.result.nb_portions} portions`} color="rgb(var(--lm-green))" />
                <div className="lm-card divide-y divide-[rgb(var(--lm-border))]">
                  {ficheData.result.ingredients?.map((ing, i) => (
                    <div key={i} className="px-4 py-2 flex justify-between">
                      <span className="text-sm text-[rgb(var(--lm-text))]">{ing.nom}</span>
                      <div className="flex gap-3">
                        <span className="text-xs font-mono text-[rgb(var(--lm-text-secondary))]">{ing.quantite}</span>
                        <span className="text-xs font-mono text-[rgb(var(--lm-green))] min-w-[40px] text-right">{ing.prix}€</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mise en place */}
              {ficheData.result.mise_en_place?.length > 0 && (
                <div>
                  <SectionTitle icon={ChefHat} title="Mise en place" color="rgb(var(--lm-gold))" />
                  <div className="lm-card p-4 space-y-2">
                    {ficheData.result.mise_en_place.map((step, i) => (
                      <p key={i} className="text-xs text-[rgb(var(--lm-text-secondary))] flex gap-2">
                        <span className="text-[rgb(var(--lm-gold))] font-mono shrink-0">{i + 1}.</span> {step}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Etapes */}
              {ficheData.result.etapes?.length > 0 && (
                <div>
                  <SectionTitle icon={BookOpen} title="Realisation" color="rgb(var(--lm-green))" />
                  <div className="space-y-2">
                    {ficheData.result.etapes.map((step, i) => (
                      <div key={i} className="lm-card p-3 flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))] flex items-center justify-center text-xs font-mono shrink-0">
                          {step.numero || i + 1}
                        </span>
                        <div className="flex-1">
                          <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{step.instruction}</p>
                          {step.duree && <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1"><Clock size={10} className="inline mr-1" />{step.duree}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dressage */}
              {ficheData.result.dressage && (
                <div className="lm-card p-3">
                  <SectionTitle icon={Star} title="Dressage" color="rgb(var(--lm-gold))" />
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{ficheData.result.dressage}</p>
                </div>
              )}

              {/* Allergenes */}
              {ficheData.result.allergenes?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertTriangle size={12} className="text-[rgb(var(--lm-orange))]" />
                  <span className="text-[10px] text-[rgb(var(--lm-orange))]">Allergenes :</span>
                  {ficheData.result.allergenes.map((a, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--lm-orange)/0.1)] text-[rgb(var(--lm-orange))]">{a}</span>
                  ))}
                </div>
              )}

              {/* Astuces */}
              {ficheData.result.astuces?.length > 0 && (
                <div className="lm-card p-3 bg-[rgb(var(--lm-green)/0.05)] border-[rgb(var(--lm-green)/0.2)]">
                  <SectionTitle icon={Sparkles} title="Astuces du chef" color="rgb(var(--lm-green))" />
                  {ficheData.result.astuces.map((a, i) => (
                    <p key={i} className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1">• {a}</p>
                  ))}
                </div>
              )}

              {/* Conservation */}
              {ficheData.result.conservation && (
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] italic">{ficheData.result.conservation}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Anti-Gaspi */}
      {activeTab === 'gaspi' && (
        <div className="space-y-4 animate-fade-in">
          <div className="lm-card p-4">
            <h3 className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Qu'est-ce qu'il vous reste ?</h3>
            <div className="flex gap-2">
              <Input value={restesInput} onChange={e => setRestesInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addReste()}
                placeholder="Ex: saumon, tomates, courgettes..."
                className="h-9 text-sm bg-[rgb(var(--lm-bg))] flex-1" data-testid="reste-input" />
              <Button onClick={addReste} size="sm" variant="outline"
                className="h-9 text-xs border-[rgb(var(--lm-border))]"
                data-testid="add-reste-btn">
                <Plus size={14} />
              </Button>
            </div>
            {restesList.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {restesList.map((r, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]"
                    data-testid={`reste-tag-${i}`}>
                    {r}
                    <button onClick={() => removeReste(i)} className="hover:text-[rgb(var(--lm-red))]"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <Button onClick={() => generate('anti-gaspi', { restes: restesList })}
            disabled={loading || restesList.length === 0}
            className="w-full bg-[rgb(var(--lm-green))] text-[rgb(var(--lm-bg))] hover:bg-[rgb(var(--lm-green)/0.9)]"
            data-testid="generate-gaspi-btn">
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Recycle size={16} className="mr-2" />}
            Trouver des idees anti-gaspi
          </Button>

          {gaspiData?.result && (
            <div className="space-y-4 animate-slide-up">
              <div>
                <SectionTitle icon={Recycle} title="Idees anti-gaspillage" color="rgb(var(--lm-green))" />
                <div className="space-y-3">
                  {gaspiData.result.suggestions?.map((s, i) => (
                    <div key={i} className="lm-card p-4" data-testid={`gaspi-suggestion-${i}`}>
                      <div className="flex justify-between items-start mb-1.5">
                        <h4 className="text-sm font-semibold text-[rgb(var(--lm-text))]">{s.nom}</h4>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))]">{s.type}</span>
                      </div>
                      <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{s.description}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {s.restes_utilises?.map((r, j) => (
                          <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--lm-green)/0.15)] text-[rgb(var(--lm-green))] font-medium">{r}</span>
                        ))}
                        {s.ingredients_supplementaires?.map((r, j) => (
                          <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))]">+ {r}</span>
                        ))}
                      </div>
                      <div className="flex gap-3 mt-2 text-[10px] text-[rgb(var(--lm-text-muted))]">
                        <span><Clock size={10} className="inline mr-0.5" />{s.temps_preparation}</span>
                        <span>Cout: <span className="font-mono text-[rgb(var(--lm-green))]">{s.cout_matiere}€</span></span>
                        <span>Prix: <span className="font-mono text-[rgb(var(--lm-gold))]">{s.prix_vente_suggere}€</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {gaspiData.result.conseil_conservation && (
                <div className="lm-card p-3 bg-[rgb(var(--lm-green)/0.05)] border-[rgb(var(--lm-green)/0.2)]">
                  <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                    <Sparkles size={12} className="inline mr-1 text-[rgb(var(--lm-green))]" />
                    {gaspiData.result.conseil_conservation}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
